import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { CapabilityProvider } from '@lemontaigu/platform-ui';
import { createSupabaseCapabilityStore } from '@lemontaigu/platform/adapters/client';
import { supabase } from './supabase.js';

/**
 * The capability composition root for the standalone PMS app (ADR-0013).
 *
 * ADDED 2026-07-28 with the L2 gate (F3). Step 8 mounted this in `admin` and
 * `reception` only — the two apps that had controls to hide. `pms` never got
 * one, which did not matter while nothing below the UI asked for an actor, and
 * became a prerequisite the moment the PMS services grew a capability gate:
 * `PmsProvider` reads `useCapabilities()`, so the context has to exist above
 * it. In `admin`, whose superset cockpit also mounts `PmsProvider`, it already
 * did.
 *
 * Named `*Provider.tsx` because that is the convention eslint.config.js matches
 * to recognise a SPA composition root — the one kind of file allowed to bind a
 * concrete client to a port.
 *
 * Mounted inside `AuthGate`: `my_capabilities()` resolves through `auth.uid()`,
 * so it needs a session.
 */
export function AppCapabilityProvider({ children }: { children: ReactNode }) {
  const store = useMemo(() => createSupabaseCapabilityStore(supabase), []);
  return <CapabilityProvider store={store}>{children}</CapabilityProvider>;
}
